import {
  mdiAccountCheck,
  mdiCalendarClock,
  mdiChevronDown,
  mdiLightningBolt,
  mdiParking,
  mdiPhone,
  mdiRefresh,
  mdiSwapHorizontal,
} from '@mdi/js';
import { Button, Divider, Menu, MenuItem, MenuList, ListItemIcon, ListItemText } from '@mui/material';
import Box from '@mui/system/Box';
import { FC, useMemo, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@mdi/react';
import {
  type OperationStatus,
  notifyBatchEditResponse,
  rowsSortingToSortDto,
  useNotificationManager,
  useSTContext,
  useSTRows,
  useWithConfirmDialog,
} from '@qre-mailing/smart-table';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
  postCommandChangeSampleResponseCodeMutation,
  postCommandResetSampleMutation,
  postCommandUnblacklistSampleRowMutation,
} from '../../api/generated-evs/@tanstack/react-query.gen';
import { defineStyles, theme } from '../../theme';

import type { FastActionContext } from './types';
import { PostponeCallDialog, PostponeCallFormData } from './PostponeCallDialog';
import { ResponseCodeChangeDialog, ResponseCodeChangeFormData } from './ResponseCodeChangeDialog';

const styles = defineStyles({
  fastActionsButton: {
    backgroundColor: theme.vars.palette.primary.main,
    color: theme.vars.palette.common.white,
    borderRadius: 1,
    padding: '4px 9px',
    fontSize: 13,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.vars.palette.primary.dark,
    },
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
  },
  menuPaper: {
    borderRadius: 2,
    boxShadow: '0px 4px 20px rgba(0,0,0,0.12)',
    border: '1px solid',
    borderColor: 'divider',
    minWidth: 260,
  },
});

export type FastActionsBarProps = {
  qreId: string;
};

export const FastActionsBar: FC<FastActionsBarProps> = ({ qreId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false);
  const [responseCodeDialogOpen, setResponseCodeDialogOpen] = useState(false);
  const table = useSTContext();
  const { rowFilterCondition, sorting, totalRows, api } = table;
  const { selectedRows } = useSTRows();
  const notificationManager = useNotificationManager();
  const selectedIds = selectedRows.map((row) => row.ipsosId);
  const resetSampleMutation = useMutation(postCommandResetSampleMutation());
  const unblacklistSampleMutation = useMutation(postCommandUnblacklistSampleRowMutation());
  const changeResponseCodeMutation = useMutation(postCommandChangeSampleResponseCodeMutation());

  const notifyBatchEdit = useCallback(
    (response: unknown) => {
      if (!notificationManager) {
        throw new Error('Fast actions require NotificationManagerProvider');
      }
      notifyBatchEditResponse(response, {
        trackOperation: notificationManager.trackOperation,
        invalidateTable: table.invalidateTable,
        toast,
      });
    },
    [notificationManager, table.invalidateTable]
  );

  const handlePostponeConfirm = useCallback(
    async (data: PostponeCallFormData) => {
      const batchEditApiCall = api.batchEditApiCall as FastActionContext['batchEditApiCall'];
      const modifications = [
        { columnName: 'AppointmentTime', value: data.appointmentTime.toISOString() },
        ...(data.appointmentName ? [{ columnName: 'AppointmentName', value: data.appointmentName }] : []),
        ...(data.appointmentTelephoneNumber
          ? [{ columnName: 'AppointmentTelephoneNumber', value: data.appointmentTelephoneNumber }]
          : []),
      ];
      const condition = {
        andConditions: rowFilterCondition?.andConditions ?? [],
        sorting: rowsSortingToSortDto(sorting),
        maxRecords: rowFilterCondition?.maxRecords ?? totalRows,
      };
      const response = await batchEditApiCall(modifications, condition, selectedIds);
      notifyBatchEdit(response);
      handleClose();
    },
    [
      api.batchEditApiCall,
      rowFilterCondition?.andConditions,
      rowFilterCondition?.maxRecords,
      sorting,
      totalRows,
      selectedIds,
      notifyBatchEdit,
    ]
  );

  const fastActionContext = useMemo<FastActionContext>(
    () => ({
      condition: {
        andConditions: rowFilterCondition?.andConditions ?? [],
        sorting: rowsSortingToSortDto(sorting),
        maxRecords: rowFilterCondition?.maxRecords ?? totalRows,
      },
      ids: selectedIds,
      batchEditApiCall: api.batchEditApiCall as FastActionContext['batchEditApiCall'],
      notifyBatchEdit,
    }),
    [
      rowFilterCondition?.andConditions,
      rowFilterCondition?.maxRecords,
      sorting,
      totalRows,
      api.batchEditApiCall,
      selectedIds,
      notifyBatchEdit,
    ]
  );

  const handleResponseCodeChangeConfirm = useCallback(
    async (data: ResponseCodeChangeFormData) => {
      const { condition } = fastActionContext;
      const response = await changeResponseCodeMutation.mutateAsync({
        body: {
          qreId,
          ipsosIds: selectedIds.map(String),
          condition: {
            andConditions: condition.andConditions,
            sorting: condition.sorting?.map((s) => ({
              field: s.field,
              direction: (s as { type?: 'ASC' | 'DESC' }).type ?? 'ASC',
            })),
            ...(condition.maxRecords != null && { maxRecords: condition.maxRecords }),
          },
          responseCode: data.responseCode,
          reason: data.reason ?? '',
        },
      });
      if (notificationManager && response?.operationId) {
        notificationManager.trackOperation({
          operationId: String(response.operationId),
          processingToast: (
            <FormattedMessage
              id="responseCodeChange.processingMessage"
              defaultMessage="Probíhá změna Response Code..."
            />
          ),
          successToast: (status: OperationStatus) => {
            const succeeded = status?.processed?.succeeded ?? 0;
            const noChanges = status?.processed?.noChanges ?? 0;
            const notExists = status?.processed?.notExists ?? 0;
            const blockedInCall = status?.processed?.blockedInCall ?? 0;
            const blockedFinalResponseCode = status?.processed?.blockedFinalResponseCode ?? 0;
            const totalBlocked = status?.processed?.blocked ?? 0;
            return (
              <>
                <strong>
                  <FormattedMessage
                    id="responseCodeChange.successMessage"
                    defaultMessage="Změna Response Code dokončena"
                  />
                </strong>
                <br />
                <FormattedMessage
                  id="responseCodeChange.succeeded"
                  defaultMessage="{succeeded} záznamů změněno"
                  values={{ succeeded }}
                />
                <br />
                <FormattedMessage
                  id="responseCodeChange.noChanges"
                  defaultMessage="{noChanges} bez změny"
                  values={{ noChanges }}
                />
                <br />
                <FormattedMessage
                  id="responseCodeChange.notExists"
                  defaultMessage="{notExists} neexistuje"
                  values={{ notExists }}
                />
                <br />
                <FormattedMessage
                  id="responseCodeChange.blocked"
                  defaultMessage="{blocked} blokováno"
                  values={{ blocked: totalBlocked }}
                />
                {blockedInCall > 0 && (
                  <>
                    <br />
                    <span style={{ marginLeft: 16 }}>
                      <FormattedMessage
                        id="responseCodeChange.blockedInCall"
                        defaultMessage="• {blockedInCall} v hovoru"
                        values={{ blockedInCall }}
                      />
                    </span>
                  </>
                )}
                {blockedFinalResponseCode > 0 && (
                  <>
                    <br />
                    <span style={{ marginLeft: 16 }}>
                      <FormattedMessage
                        id="responseCodeChange.blockedFinalResponseCode"
                        defaultMessage="• {blockedFinalResponseCode} kvůli finálnímu response kódu"
                        values={{ blockedFinalResponseCode }}
                      />
                    </span>
                  </>
                )}
              </>
            );
          },
          errorToast: (
            <FormattedMessage
              id="responseCodeChange.error"
              defaultMessage="Změna Response Code se nezdařila. Zkuste to znovu."
            />
          ),
          isWarning: (status: OperationStatus) => (status?.processed?.blocked ?? 0) > 0,
        });
      }
      handleClose();
    },
    [fastActionContext, qreId, selectedIds, changeResponseCodeMutation, notificationManager]
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const withConfirmDialog = useWithConfirmDialog();

  return (
    <>
      <Button
        size="small"
        variant="contained"
        onClick={handleOpen}
        sx={styles.fastActionsButton}
        startIcon={
          <Box sx={styles.iconWrap}>
            <Icon path={mdiLightningBolt} size={1} />
          </Box>
        }
        endIcon={
          <Box sx={styles.iconWrap}>
            <Icon path={mdiChevronDown} size={1} />
          </Box>
        }
      >
        <FormattedMessage id="table.fastBulkActions.button" defaultMessage="Fast Actions" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: styles.menuPaper,
          },
        }}
      >
        <MenuList dense disablePadding>
          <MenuItem
            onClick={() => {
              handleClose();
              withConfirmDialog({
                title: (
                  <FormattedMessage
                    id="park.confirm.title"
                    defaultMessage="Tato akce zařadí záznamy na parkoviště, opravdu chcete pokračovat?"
                  />
                ),
                confirmButtonText: <FormattedMessage id="common.confirm" defaultMessage="Potvrdit" />,
                cancelButtonText: <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />,
                onConfirm: async () => {
                  const modifications = [{ columnName: 'Parking', value: 1 }];
                  const response = await fastActionContext.batchEditApiCall(
                    modifications,
                    fastActionContext.condition,
                    fastActionContext.ids
                  );
                  fastActionContext.notifyBatchEdit(response);
                  handleClose();
                },
              })();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiParking} size={1} />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="park.button" defaultMessage="Zařadit na parkoviště" />} />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              withConfirmDialog({
                title: (
                  <FormattedMessage
                    id="unpark.confirm.title"
                    defaultMessage="Tato akce zařadí záznamy k volání, opravdu chcete pokračovat?"
                  />
                ),
                confirmButtonText: <FormattedMessage id="common.confirm" defaultMessage="Potvrdit" />,
                cancelButtonText: <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />,
                onConfirm: async () => {
                  const modifications = [{ columnName: 'Parking', value: 0 }];
                  const response = await fastActionContext.batchEditApiCall(
                    modifications,
                    fastActionContext.condition,
                    fastActionContext.ids
                  );
                  fastActionContext.notifyBatchEdit(response);
                  handleClose();
                },
              })();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiPhone} size={1} />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="unpark.button" defaultMessage="Zařadit k volání" />} />
          </MenuItem>
          <Divider />

          <MenuItem
            onClick={() => {
              handleClose();
              setPostponeDialogOpen(true);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiCalendarClock} size={1} />
            </ListItemIcon>
            <ListItemText
              primary={<FormattedMessage id="postponeCall.button" defaultMessage="Odložit volání na ..." />}
            />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose();
              withConfirmDialog({
                title: (
                  <FormattedMessage
                    id="reset.confirm.title"
                    defaultMessage="Tato akce resetuje záznamy. Opravdu chcete pokračovat?"
                  />
                ),
                confirmButtonText: <FormattedMessage id="common.confirm" defaultMessage="Potvrdit" />,
                cancelButtonText: <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />,
                onConfirm: async () => {
                  const { condition } = fastActionContext;
                  const response = await resetSampleMutation.mutateAsync({
                    body: {
                      qreId,
                      ipsosIds: selectedIds.map(String),
                      condition: {
                        andConditions: condition.andConditions,
                        sorting: condition.sorting?.map((s) => ({
                          field: s.field,
                          direction: (s as { type?: 'ASC' | 'DESC' }).type ?? 'ASC',
                        })),
                        ...(condition.maxRecords != null && { maxRecords: condition.maxRecords }),
                      },
                    },
                  });
                  if (notificationManager && response?.operationId) {
                    notificationManager.trackOperation({
                      operationId: String(response.operationId),
                      processingToast: (
                        <FormattedMessage id="reset.processingMessage" defaultMessage="Probíhá reset..." />
                      ),
                      successToast: (status: OperationStatus) => (
                        <>
                          <strong>
                            <FormattedMessage id="reset.successMessage" defaultMessage="Reset dokončen!" />
                          </strong>
                          <br />
                          <FormattedMessage
                            id="reset.rowsProcessed"
                            defaultMessage="{succeeded} záznamů zpracováno"
                            values={{ succeeded: status?.processed?.succeeded ?? 0 }}
                          />
                          <br />
                          <FormattedMessage
                            id="reset.succeeded"
                            defaultMessage="{failed} záznamů selhalo"
                            values={{ failed: status?.processed?.failed ?? 0 }}
                          />
                          <br />
                          <FormattedMessage
                            id="reset.notExists"
                            defaultMessage="{notExists} záznamů neexistuje"
                            values={{ notExists: status?.processed?.notExists ?? 0 }}
                          />
                          <br />
                          <FormattedMessage
                            id="reset.noChanges"
                            defaultMessage="{noChanges} záznamů nebylo resetováno"
                            values={{ noChanges: status?.processed?.noChanges ?? 0 }}
                          />
                        </>
                      ),
                      errorToast: (
                        <FormattedMessage id="reset.error" defaultMessage="Reset se nezdařil. Zkuste to znovu." />
                      ),
                    });
                  }
                  handleClose();
                },
              })();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiRefresh} size={1} />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage id="reset.button" defaultMessage="Resetovat záznam" />} />
          </MenuItem>
          <Divider />

          <MenuItem
            onClick={() => {
              handleClose();
              withConfirmDialog({
                title: (
                  <FormattedMessage
                    id="unblacklist.confirm.title"
                    defaultMessage="Tato akce použije kontakty bez blacklistovaného čísla. Opravdu chcete pokračovat?"
                  />
                ),
                confirmButtonText: <FormattedMessage id="common.confirm" defaultMessage="Potvrdit" />,
                cancelButtonText: <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />,
                onConfirm: async () => {
                  const { condition } = fastActionContext;
                  const response = await unblacklistSampleMutation.mutateAsync({
                    body: {
                      qreId,
                      ipsosIds: selectedIds.map(String),
                      condition: {
                        andConditions: condition.andConditions,
                        sorting: condition.sorting?.map((s) => ({
                          field: s.field,
                          direction: (s as { type?: 'ASC' | 'DESC' }).type ?? 'ASC',
                        })),
                        ...(condition.maxRecords != null && { maxRecords: condition.maxRecords }),
                      },
                    },
                  });
                  if (notificationManager && response?.operationId) {
                    notificationManager.trackOperation({
                      operationId: String(response.operationId),
                      processingToast: (
                        <FormattedMessage id="unblacklist.processingMessage" defaultMessage="Probíhá unblacklist..." />
                      ),
                      successToast: (status: OperationStatus) => {
                        const noChanges = status?.processed?.noChanges ?? 0;
                        const succeeded = status?.processed?.succeeded ?? 0;
                        const notExists = status?.processed?.notExists ?? 0;
                        return (
                          <>
                            <strong>
                              <FormattedMessage id="unblacklist.successMessage" defaultMessage="Unblacklist dokončen" />
                            </strong>
                            <br />
                            <FormattedMessage
                              id="unblacklist.ready"
                              defaultMessage="{succeeded} kontaktů použitých bez blacklistovaného čísla"
                              values={{ succeeded }}
                            />
                            <br />
                            <FormattedMessage
                              id="unblacklist.noChanges"
                              defaultMessage="{noChanges} bez změny"
                              values={{ noChanges }}
                            />
                            <br />
                            <FormattedMessage
                              id="unblacklist.notExists"
                              defaultMessage="{notExists} neexistuje"
                              values={{ notExists }}
                            />
                          </>
                        );
                      },
                      errorToast: (
                        <FormattedMessage
                          id="unblacklist.error"
                          defaultMessage="Unblacklist se nezdařil. Zkuste to znovu."
                        />
                      ),
                    });
                  }
                  handleClose();
                },
              })();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiAccountCheck} size={1} />
            </ListItemIcon>
            <ListItemText
              primary={
                <FormattedMessage id="unblacklist.button" defaultMessage="Použít kontakt bez blacklistovaného čísla" />
              }
            />
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose();
              setResponseCodeDialogOpen(true);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
              <Icon path={mdiSwapHorizontal} size={1} />
            </ListItemIcon>
            <ListItemText
              primary={<FormattedMessage id="responseCodeChange.button" defaultMessage="Změna Response Code" />}
            />
          </MenuItem>
        </MenuList>
      </Menu>
      <PostponeCallDialog
        open={postponeDialogOpen}
        onClose={() => setPostponeDialogOpen(false)}
        onConfirm={handlePostponeConfirm}
      />
      <ResponseCodeChangeDialog
        open={responseCodeDialogOpen}
        qreId={qreId}
        onClose={() => setResponseCodeDialogOpen(false)}
        onConfirm={handleResponseCodeChangeConfirm}
      />
    </>
  );
};
